import { defaultDocumentsLimit, getDocuments } from '3-foundation/Documents/repository';
import { usePageContext } from '3-foundation/PageContent/PageContext';
import { useCallback, useEffect, useState } from 'react';
import { UI_DocumentOverview } from 'ui/components/3-organisms/DocumentOverview/DocumentOverview';
import { UI_Container } from 'ui/components/4-habitats/Container/Container';
import { useSiteContext } from '3-foundation/Site/SiteContext';
import { buildTagHierarchyFromFacets } from './buildTagHierarchyFromFacets';
import { DocumentFilters } from './DocumentFilters';
import { useDictionaryContext } from '3-foundation/Dictionary/DictionaryContext';
import { useRouter } from 'next/router';

export const DocumentSearchResults = ({
	serverSideDocuments,
	facets,
	totalDocuments,
	labels,
	tags,
}: {
	serverSideDocuments: Content.DocumentCatalogPageProperties['documents'];
	facets: Content.DocumentCatalogPageProperties['facets'];
	totalDocuments: Content.DocumentCatalogPageProperties['totalDocuments'];
	labels: {
		dateFilter?: string;
		subjectFilter?: string;
		statusFilter?: string;
		typeFilter?: string;
	};
	tags: Site.Tags;
}) => {
	const pageContext = usePageContext();
	const siteContext = useSiteContext();
	const dictionary = useDictionaryContext();
	const { locale } = useRouter();

	const [documents, setDocuments] = useState(serverSideDocuments);
	const [filters, setFilters] = useState<{
		dateTags: Content.DocumentFacet[];
		subjectTags: (Site.SiteTagFolder | Site.SiteTag)[];
		statusTags: (Site.SiteTagFolder | Site.SiteTag)[];
		typeTags: (Site.SiteTagFolder | Site.SiteTag)[];
	}>(null);
	const [selectedFilters, setSelectedFilters] = useState({
		dateTags: [],
		subjectTags: [],
		statusTags: [],
		typeTags: [],
	});
	const [totalResults, setTotalResults] = useState(totalDocuments);
	const [fetchClientSide, setFetchClientSide] = useState(false);

	useEffect(() => {
		setFilters({
			subjectTags: buildTagHierarchyFromFacets(tags.subjectTags, facets.subject),
			statusTags: buildTagHierarchyFromFacets(tags.statusTags, facets.status),
			typeTags: buildTagHierarchyFromFacets(tags.typeTags, facets.type),
			dateTags: facets.date,
		});
	}, [tags, facets]);

	const fetchDocuments = useCallback(
		async ({ limit }: { limit?: number }) => {
			try {
				const noneSelected = Object.values(selectedFilters).every((tags) => tags.length === 0);
				if (noneSelected) return setDocuments(serverSideDocuments); // Default back to serverSideDocuments without fetching
				const dataResponse = await getDocuments({
					filters: {
						parentId: pageContext?.id,
						tagsSubject: selectedFilters.subjectTags?.length ? selectedFilters.subjectTags : null,
						tagsStatus: selectedFilters.statusTags?.length ? selectedFilters.statusTags : null,
						tagsType: selectedFilters.typeTags?.length ? selectedFilters.typeTags : null,
						tagsDate: selectedFilters.dateTags?.length ? selectedFilters.dateTags : null,
					},
					limit,
					locale,
					siteId: siteContext?.root?.id,
				});
				setTotalResults(dataResponse.totalDocuments);
				setDocuments(dataResponse?.documents);
			} catch (error) {
				console.error('Failed to fetch documents:', error);
			}
		},
		[pageContext?.id, selectedFilters, serverSideDocuments, locale, siteContext?.root?.id],
	);

	useEffect(() => {
		if (fetchClientSide) fetchDocuments({});
	}, [fetchClientSide, fetchDocuments]);

	const handleFilterChange = (group: 'dateTags' | 'subjectTags' | 'statusTags' | 'typeTags', selectedValues: string[]) => {
		setFetchClientSide(true);
		setSelectedFilters((prev) => ({
			...prev,
			[group]: selectedValues,
		}));
	};

	return (
		<UI_Container width="Standard">
			<DocumentFilters filters={filters} labels={labels} selectedFilters={selectedFilters} handleFilterChange={handleFilterChange} />
			<UI_DocumentOverview
				cards={documents?.map((document) => {
					return {
						image: document.image,
						kicker: dictionary.getDocument('Document'),
						title: document.title,
						date: {
							label: dictionary.getDocument('DocumentDate'),
							value: document.date,
						},
						link: { name: dictionary.getEvent('MoreInfo'), url: document.link.url },
					};
				})}
				paginationText={documents.length < totalResults ? dictionary.getSearch('LoadMore') : null}
				requestMoreCards={() => fetchDocuments({ limit: documents.length + defaultDocumentsLimit })}
			/>
		</UI_Container>
	);
};
